import {
    trackEvent,
    trackHyperReportCompleted,
    trackAccountCreated,
} from '@/lib/utils/ga';
import { sendNewUserNotification } from '@/lib/utils/slack';

// Types for analytics events
export interface AnalyticsEvent {
    name: string;
    properties?: Record<string, any>;
    userId?: string;
    email?: string;
    signupSource?: string;
    status?: 'pending_verification' | 'verified';
}

// Types for specific events
export interface HyperReportCompletedEvent {
    userId: string;
    email?: string;
    name?: string;
    hyperScore: number;
    planTier: 'free' | 'pro' | 'team';
}

export interface AccountCreatedEvent {
    userId: string;
    email: string;
    signupSource:
        | 'organic'
        | 'google_ads'
        | 'referral'
        | 'paid_social'
        | 'paid_search';
}

export interface UserVerifiedEvent {
    userId: string;
    email: string;
    signupSource?: string;
}

export interface NameSetEvent {
    userId: string;
    email: string;
    name: string;
}

export interface LoginEvent {
    userId: string;
    email: string;
}

// Analytics service class
export class AnalyticsService {
    private static instance: AnalyticsService;
    private posthog: any = null;

    private constructor() {}

    public static getInstance(): AnalyticsService {
        if (!AnalyticsService.instance) {
            AnalyticsService.instance = new AnalyticsService();
        }
        return AnalyticsService.instance;
    }

    // Initialize PostHog client (called from providers)
    public setPostHogClient(posthogClient: any) {
        this.posthog = posthogClient;
    }

    // Generic event tracking
    public async trackEvent(event: AnalyticsEvent): Promise<void> {
        try {
            // Google Analytics
            if (event.properties) {
                trackEvent(event.name, event.properties);
            }

            // PostHog
            if (this.posthog && event.userId) {
                this.posthog.identify(event.userId);
                this.posthog.capture(event.name, {
                    ...event.properties,
                    $set: {
                        email: event.email,
                    },
                });
            }
        } catch (error) {
            console.error('Failed to track event:', error);
        }
    }

    // Hyper Report Completed
    public async trackHyperReportCompleted(
        event: HyperReportCompletedEvent,
    ): Promise<void> {
        try {
            // Google Analytics
            trackHyperReportCompleted(
                event.userId,
                event.hyperScore,
                event.planTier,
            );

            // PostHog
            if (this.posthog) {
                this.posthog.identify(event.userId);
                this.posthog.capture('hyper_report_completed', {
                    user_id: event.userId,
                    hyper_score: event.hyperScore,
                    plan_tier: event.planTier,
                    timestamp: new Date().toISOString(),
                });
            }
        } catch (error) {
            console.error('Failed to track hyper report completed:', error);
        }
    }

    // Account Created
    public async trackAccountCreated(
        event: AccountCreatedEvent,
    ): Promise<void> {
        try {
            // Google Analytics
            trackAccountCreated(event.userId, event.signupSource);

            // PostHog
            if (this.posthog) {
                this.posthog.identify(event.userId);
                this.posthog.capture('account_created', {
                    user_id: event.userId,
                    email: event.email,
                    signup_source: event.signupSource,
                    timestamp: new Date().toISOString(),
                });
            }

            // Slack notification
            try {
                await sendNewUserNotification(
                    event.userId,
                    event.email,
                    event.signupSource,
                    'pending_verification',
                );
            } catch (slackError) {
                console.error(
                    'Failed to send Slack notification for account created:',
                    slackError,
                );
            }
        } catch (error) {
            console.error('Failed to track account created:', error);
        }
    }

    // User Verified (includes Slack notification)
    public async trackUserVerified(event: UserVerifiedEvent): Promise<void> {
        try {
            // Google Analytics
            trackEvent('user_verified', {
                user_id: event.userId,
                email: event.email,
                signup_source: event.signupSource || 'unknown',
                timestamp: new Date().toISOString(),
            });

            // PostHog
            if (this.posthog) {
                this.posthog.identify(event.userId);
                this.posthog.capture('user_verified', {
                    user_id: event.userId,
                    email: event.email,
                    signup_source: event.signupSource || 'unknown',
                    timestamp: new Date().toISOString(),
                });
            }

            // Slack notification
            await sendNewUserNotification(
                event.userId,
                event.email,
                event.signupSource || 'verified',
                'verified',
            );
        } catch (error) {
            console.error('Failed to track user verified:', error);
        }
    }

    // Name Set (includes Slack notification)
    public async trackNameSet(event: NameSetEvent): Promise<void> {
        try {
            // PostHog
            if (this.posthog) {
                this.posthog.identify(event.userId);
                this.posthog.capture('name_set', {
                    user_id: event.userId,
                    email: event.email,
                    name: event.name,
                    timestamp: new Date().toISOString(),
                });
            }

            // Slack notification (distinct for name set)
            await sendNewUserNotification(
                event.userId,
                event.email,
                'name_set',
                'name_set',
                event.name,
            );
        } catch (error) {
            console.error('Failed to track name set:', error);
        }
    }

    // Page view tracking
    public trackPageView(url: string, userId?: string): void {
        try {
            // PostHog
            if (this.posthog) {
                if (userId) {
                    this.posthog.identify(userId);
                }
                this.posthog.capture('$pageview', { $current_url: url });
            }
        } catch (error) {
            console.error('Failed to track page view:', error);
        }
    }

    // User identification
    public identifyUser(
        userId: string,
        properties?: Record<string, any>,
    ): void {
        try {
            if (this.posthog) {
                this.posthog.identify(userId, properties);
            }
        } catch (error) {
            console.error('Failed to identify user:', error);
        }
    }

    public async trackLogin(event: LoginEvent): Promise<void> {
        try {
            // Google Analytics
            trackEvent('user_login', {
                user_id: event.userId,
                email: event.email,
                timestamp: new Date().toISOString(),
            });

            // PostHog
            if (this.posthog) {
                this.posthog.identify(event.userId);
                this.posthog.capture('user_login', {
                    user_id: event.userId,
                    email: event.email,
                    timestamp: new Date().toISOString(),
                });
            }

            // Slack notification
            await sendNewUserNotification(
                event.userId,
                event.email,
                'login',
                'verified',
            );
        } catch (error) {
            console.error('Failed to track login:', error);
        }
    }
}

// Export singleton instance
export const analytics = AnalyticsService.getInstance();
